<template>
  <div class="filter-input-ui">
    <VInput
      v-model="search"
      :config="{
        label: label,
        type: 'string',
        inputMode: 'text',
      }"
      :class="variantClassName"
      @input="handleUpdateSearch"
      @focus="setIsHintShown(true)"
      @blur="setIsHintShown(false)"
    />
    <Transition name="tooltip">
      <div v-show="isHintShown" class="filter-input-ui__hint">
        <VFallbackText class="hint">
          {{ hint }}
        </VFallbackText>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
  // Config
  import { MIN_CHARS_SEARCH, VALUE_UPDATE_DELAY_IN_MS } from '~/misc/filter-input/config';

  // Components
  import VInput from '~/misc/v-input';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';

  // Utils
  import { debounce } from 'lodash-es';
  import { pluralize } from '~/misc/utils/number';

  const props = defineProps({
    modelValue: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },
    label: {
      type: String,
      default: '',
    },
    variantClassName: {
      type: String,
      default: '',
    },
  });

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string | undefined): void;
  }>();

  const search: Ref<string> = ref<string>(props.modelValue ?? '');

  const isHintShown: Ref<boolean> = ref<boolean>(false);

  const hint: ComputedRef<string> = computed<string>(() => {
    const searchLength: number = search.value.trim().length;

    if (!searchLength) {
      return `Min ${MIN_CHARS_SEARCH} ${pluralize(MIN_CHARS_SEARCH, 'char')}`;
    }

    const charsLeft: number = Math.max(MIN_CHARS_SEARCH - searchLength, 0);

    if (charsLeft) {
      return `${charsLeft} more ${pluralize(MIN_CHARS_SEARCH, 'char')} left`;
    }

    return '';
  });

  const setIsHintShown = (state: boolean) => {
    isHintShown.value = state;
  };

  const handleUpdateSearch = debounce((event: InputEvent) => {
    const targetValue: string = (event.target as HTMLInputElement).value.trim();
    const value: string | undefined = targetValue === '' ? undefined : targetValue;

    (value === undefined || value.length >= MIN_CHARS_SEARCH) && emit('update:modelValue', value);
  }, VALUE_UPDATE_DELAY_IN_MS);
</script>
